/* eslint-disable @typescript-eslint/no-misused-promises */
import { useForm } from "react-hook-form";
import {
  fullTimeOfferForm,
  FullTimeOfferForm as FullTimeOfferFormSchema,
} from "./schema";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { CurrencyInput } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { Button } from "@/components/ui/button";

import { NewOffer } from "@/types";

const FullTimeOfferForm = ({
  sender,
  isPending,
  error,
  isCounter,
  mutate,
  onCancel,
}: {
  sender: "company" | "candidate";
  isPending: boolean;
  error: Error | null;
  isCounter?: boolean;
  mutate: (data: NewOffer) => void;
  onCancel: () => void;
}) => {
  const form = useForm<FullTimeOfferFormSchema>({
    resolver: async (data, context, opts) => {
      return zodResolver(fullTimeOfferForm)(data, context, opts);
    },
  });

  function onSubmit(data: FullTimeOfferFormSchema) {
    if (isPending) return;

    mutate({
      terms: [
        {
          termType: "full-time",
          totalAmountCents: data.totalAmountCents,
          startDate: data.startDate.toISOString(),
        },
      ],
      note: data.note,
    });
  }

  return (
    <Form {...form}>
      {error && (
        <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
          Something went wrong when making a counter offer, please try again
          later.
        </p>
      )}
      <form className="space-y-4 w-full" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="totalAmountCents"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Salary</FormLabel>
              <FormControl>
                <CurrencyInput
                  valueCents={field.value}
                  onValueCentsChange={field.onChange}
                  minCents={0}
                  placeholder="100,000"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Start date</FormLabel>
              <FormControl>
                <DatePicker
                  value={field.value}
                  onChange={field.onChange}
                  modal
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Note to the {sender === "company" ? "candidate" : "employer"}{" "}
                (optional)
              </FormLabel>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="h-[1px] bg-[#E4E7EC] w-full" />
        <div className="!mb-1 flex justify-between">
          <Button variant="stroke" className="mt-2" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="success"
            type="submit"
            className="mt-2"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={form.handleSubmit(onSubmit)}
          >
            {isCounter ? "Submit Counter Offer" : "Submit Offer"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default FullTimeOfferForm;
